import type { AlertConfig } from './types';

// Confidence level options used in filters and chart dataset selection
export enum ConfidenceLevel {
  Level75 = '75',
  Level95 = '95',
  Level99 = '99',
}

// Chart type options for the main simulation chart
export enum ChartType {
  Line        = 'line',
  Bar         = 'bar',
  Doughnut    = 'doughnut',
  Area        = 'area',
  Stepped     = 'stepped',
  Scatter     = 'scatter',
  StackedArea = 'stackedArea',
}

// Dataset labels used in chart legends and tooltip callbacks
export enum DatasetLabel {
  MtM              = 'MtM',
  MeanMtM          = 'Current MtM ',
  BreachThreshold  = 'Breach Threshold',
  Plus1SD          = '+1 SD',
  Minus1SD         = '-1 SD',
  Plus2SD          = '+2 SD',
  Minus2SD         = '-2 SD',
  Plus3SD          = '+3 SD',
  Minus3SD         = '-3 SD',
  // Maturity chart labels
  MaturityMinus1SD = '-1SD',
  MaturityMinus2SD = '-2SD',
  MaturityMinus3SD = '-3SD',
  MaturityPlus1SD  = '+1SD',
  MaturityPlus2SD  = '+2SD',
  MaturityPlus3SD  = '+3SD',
}

// Keys of PortfolioDataPoint used as field references
export enum PortfolioField {
  Date        = 'date',
  Timestamp   = 'timestamp',
  Median      = 'median',
  Conf75Low   = 'conf75_low',
  Conf75High  = 'conf75_high',
  Conf95Low   = 'conf95_low',
  Conf95High  = 'conf95_high',
  Conf99Low   = 'conf99_low',
  Conf99High  = 'conf99_high',
}

// Tab identifiers for chart tab navigation
export enum TabId {
  MtM = 'mtm',
}

// Chart title strings
export enum ChartTitle {
  SimulatedMtM = 'Simulated MtM Value with CSA Threshold',
  SpotRatePrefix = 'Spot Rate — ',
}

// Opacity hex suffixes appended to a hex color string (e.g. "#ff0000" + ColorOpacity.Area = "#ff000040")
export enum ColorOpacity {
  Full    = 'FF',  // 100 % — solid fill
  High    = 'BB',  // ~73 % — maturity bar fill
  Medium  = '99',  // ~60 % — maturity bar fill (wider SD)
  Low     = '80',  // ~50 % — maturity bar fill (outermost SD)
  Tooltip = 'F2',  // ~95 % — tooltip background
  Grid    = '60',  // ~38 % — axis grid lines
  Area    = '40',  // ~25 % — area / stacked-area fill
  Normal  = '1a',  // ~10 % — standard semi-transparent fill
}

// Maps semantic chart color roles to theme.colors property names
export enum ChartColorKey {
  Median        = 'turquoiseicon',
  Conf95        = 'blueIcon',
  Conf75        = 'green',
  Conf99        = 'yellow',
  Threshold     = 'red',
  Breach        = 'danger',
  Purple        = 'violetIcon',
  Background    = 'background',
  TextPrimary   = 'textPrimary',
  TextSecondary = 'textSecondary',
  Divider       = 'divider',
}

// CSS display values used to toggle chart panel visibility
export enum DisplayMode {
  Visible = 'block',
  Hidden  = 'none',
}

// Chart rendering numeric constants
export const CHART_BORDER_WIDTH_BAR  = 1;
export const CHART_BORDER_WIDTH_LINE = 2;
export const CHART_BORDER_WIDTH_MEAN = 3;
export const CHART_POINT_RADIUS_SCATTER = 6;
export const CHART_POINT_RADIUS_DEFAULT = 3;
export const CHART_POINT_RADIUS_BAR     = 0;
export const CHART_POINT_RADIUS_HIDDEN  = 0;  // elements.point.radius — hides all points globally
export const CHART_FONT_SIZE_LEGEND   = 14;
export const CHART_FONT_SIZE_MATURITY = 13;
export const CHART_FONT_SIZE_AXIS     = 12;
export const CHART_TOOLTIP_BORDER_WIDTH = 1;

// Border dash patterns [dashLength, gapLength]
export const BORDER_DASH_DASHED: [number, number] = [5, 5];
export const BORDER_DASH_MEAN:   [number, number] = [10, 5];

// Default / magic number constants
export const MATURITY_ZERO_THRESHOLD = 1;      // values below this are treated as "maturity reached"
export const MEAN_BAR_DELTA_RATIO = 0.006;     // thin mean bar height = 0.6% of median absolute value
export const MEAN_BAR_DELTA_MIN = 1_000;       // minimum delta when median is near zero
export const Y_AXIS_PADDING_RATIO = 1.05;      // 5% extra room beyond the outermost SD band
export const NUMBER_OF_SIMULATIONS_OPTIONS = [5, 50, 100, 500] as const;
export const DEFAULT_NUMBER_OF_SIMULATIONS = 100;

// Shown wherever the breach threshold is unavailable because no CSA threshold
// is configured for the selected portfolio.
export const NO_CSA_THRESHOLD_LABEL = 'No CSA threshold configured';
export const NO_CSA_THRESHOLD_TOOLTIP =
  'No CSA threshold is configured for the selected classification tag / entity and counterparties, so breach alerts are unavailable. Contact your FL consultant to configure one.';

// Default alert configuration. `threshold: null` = no CSA threshold configured
// for the current selection, so the breach alert is unavailable until the
// CSA-threshold API returns one.
export const DEFAULT_ALERT_CONFIG: AlertConfig = {
  threshold:    null,
  enabled:      true,
  emailSent:    false,
  emailSending: false,
};
